import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { EmSwash } from "../components/EmSwash";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { FoundingDonorGiveDialog } from "../components/FoundingDonorGiveDialog";
import { SEO } from "../lib/SEO";
import { fetchFoundingDonors, groupByLastNameLetter, SEED_FALLBACK } from "@/lib/foundingDonors";
import { FoundingShareRow } from "../components/FoundingShareRow";
import { LitGround } from "../components/PaperLight";

/**
 * FoundingDonors — the founding donors wall.
 *
 * Public route: /founding
 *
 * Design principles (per marketing exec review 2026-07):
 *   - NO ambient logo pattern behind the donor cards. Names deserve a hush.
 *   - Photos render at donor's choice — full color by default, black &
 *     white if the donor toggles it. No forced sepia.
 *   - One alphabetical grid (audit item M7). A small italic letter in the
 *     corner of the first card of each initial keeps the A-to-Z wayfinding
 *     without a divider per letter; the palette dashes appear once, before
 *     the footer.
 */
export default function FoundingDonors() {
  const [donors, setDonors] = useState(SEED_FALLBACK);
  const [giveOpen, setGiveOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchFoundingDonors().then((data) => {
      if (cancelled) return;
      setDonors(data.donors || []);
    });
    return () => { cancelled = true; };
  }, []);

  const groups = groupByLastNameLetter(donors);

  return (
    <main data-testid="founding-donors-page" className="bg-sand">
      <SEO
        title="Founding Donors — My Rich Aunt"
        description="Meet the people who believed first — the founding donors backing My Rich Aunt before campaigns went public. Add your name to the wall with a tax-deductible gift."
        path="/founding"
        image={`${process.env.REACT_APP_BACKEND_URL}/api/og/founding.jpg`}
        imageAlt="The Founding Donors wall — the names of the people who believed first"
      />
      <Navbar onStart={() => setGiveOpen(true)} primaryLabel="Add your name" />

      {/* Masthead — the Founding Aunties and Uncles */}
      <LitGround>
      <section
        data-testid="founding-masthead"
        className="mx-auto max-w-7xl px-6 pt-32 lg:px-8 lg:pt-48"
      >
        <FadeUp tier="sm">
          <p
            data-testid="founding-eyebrow"
            className="text-center text-[13px] font-medium uppercase tracking-[0.28em] text-olive"
          >
            The Founding Aunties and Uncles
          </p>
        </FadeUp>

        <FadeUp>
          <h1
            data-testid="founding-headline"
            className="mt-6 max-w-5xl font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.95] tracking-tight text-ink"
          >
            The names who <EmSwash>believed first.</EmSwash>
          </h1>
        </FadeUp>

        {/* Editorial hairline under the headline, echoing the top doodle bookend */}
        <div className="mt-8 h-px w-full bg-ink/15" />

        {/* Dek + How-the-roll-works aside */}
        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[2fr_1fr] lg:gap-14">
          <FadeUp>
            <p className="max-w-3xl font-serif text-3xl italic leading-relaxed text-ink">
              Before campaigns went live. Before donations opened. Before there was proof they'd be right, these aunties and uncles chose to be in. The roll is theirs. Their photos, their tributes, their choice to stand up first.
            </p>
          </FadeUp>
          <FadeUp>
            <aside className="max-w-sm border-l border-border pl-6 text-[13px] leading-relaxed text-olive lg:pl-8">
              <strong className="mb-1 block font-serif text-base font-semibold not-italic text-ink">
                How the roll works
              </strong>
              One name per gift. One line, permanent. Add a tribute or a photo from when you were the student, or keep it just a name. Either way, you're on the wall for good.
              <span className="mt-4 block border-t border-border pt-3 text-[13px] font-semibold uppercase tracking-[0.22em] text-olive/80">
                Open now through beta launch
              </span>
            </aside>
          </FadeUp>
        </div>

        {/* CTA row */}
        <FadeUp>
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <button
              type="button"
              data-testid="founding-add-name-button"
              onClick={() => setGiveOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand transition hover:bg-forest"
            >
              Add your name to the wall
              <span aria-hidden>→</span>
            </button>
            <span className="max-w-md font-serif text-3xl italic leading-snug text-olive">
              Takes about two minutes. Your spot on the roll is held the moment you finish.
            </span>
          </div>
        </FadeUp>
      </section>
      </LitGround>

      {/* The roll — one alphabetical grid; a small letter marker opens each new initial */}
      <section className="mx-auto mt-16 max-w-7xl px-6 pb-16 lg:px-8" data-testid="founding-roll">
        <div className="flex items-baseline justify-between border-b border-ink/15 pb-4">
          <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">The roll · A to Z</p>
          <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive" data-testid="founding-roll-count">
            {donors.length} {donors.length === 1 ? "name" : "names"}
          </p>
        </div>
        <div className="mt-6" data-testid="founding-share-section">
          <FoundingShareRow count={donors.length} />
        </div>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {groups.flatMap((group) =>
            group.donors.map((donor, i) => (
              <DonorCard key={`${donor.full_name}-${i}`} donor={donor} idx={i} letter={i === 0 ? group.letter : null} />
            ))
          )}
        </div>
      </section>

      <Footer
        onStart={() => (window.location.href = "/")}
        onDonate={() => setGiveOpen(true)}
        primaryLabel="Back to home"
        secondaryLabel="Add your name"
      />

      <FoundingDonorGiveDialog open={giveOpen} onOpenChange={setGiveOpen} />
    </main>
  );
}

// ── Donor card ────────────────────────────────────────────────────────

const DonorCard = ({ donor, idx, letter }) => {
  const hasPhoto = Boolean(donor.photo_url);
  const hasTribute = Boolean((donor.tribute || "").trim());
  const school = donor.alma_mater ? donor.alma_mater : null;

  return (
    <motion.article
      data-testid="founding-donor-card"
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: (idx % 6) * 0.04, ease: [0.22, 0.61, 0.36, 1] }}
      className="relative rounded-lg border border-border bg-bone p-5 shadow-sm"
    >
      {letter && (
        <span
          data-testid={`founding-letter-${letter}`}
          aria-label={`Names starting with ${letter}`}
          className="absolute right-4 top-3 font-serif text-3xl italic leading-none text-olive/60"
        >
          {letter}
        </span>
      )}
      {hasPhoto && (
        <div className="mb-4 overflow-hidden rounded-md">
          <img
            src={donor.photo_url}
            alt={`${donor.full_name} in college`}
            loading="lazy"
            data-testid="founding-donor-photo"
            className={`aspect-[4/5] w-full object-cover ${donor.photo_filter === "bw" ? "grayscale" : ""}`}
          />
        </div>
      )}

      <h3 className="font-serif text-3xl leading-tight text-ink">
        {donor.full_name}
      </h3>

      {school && (
        <p className="mt-1 text-[13px] font-semibold uppercase tracking-[0.16em] text-olive">
          {school}
        </p>
      )}

      {hasTribute && (
        <>
          <div className="mt-4 h-px bg-border" />
          <p className="mt-4 font-serif italic leading-snug text-ink">
            &ldquo;{donor.tribute}&rdquo;
          </p>
        </>
      )}

      {donor.joined_date && (
        <>
          <div className="mt-4 h-px bg-border" />
          <p className="mt-3 text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
            Joined {formatJoined(donor.joined_date)}
          </p>
        </>
      )}
    </motion.article>
  );
};

const formatJoined = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
